import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Settings,
  User,
  Mail,
  Link,
  Unlink,
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  ClipboardList,
  Plus,
  Trash2,
  GripVertical,
  Plug,
  Key,
  MessageSquare,
  Eye,
  EyeOff,
  UserPlus,
  Send,
  RefreshCw,
  Shield,
  Clock,
  X
} from 'lucide-react';

const SettingsPage = () => {
  const { user, checkAuth } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [userSettings, setUserSettings] = useState(null);
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [integrationSettings, setIntegrationSettings] = useState(null);
  const [invites, setInvites] = useState([]);
  
  // Form state
  const [name, setName] = useState('');
  const [emailSignature, setEmailSignature] = useState('');
  const [dailyEmailCap, setDailyEmailCap] = useState(100);
  
  // Invite form state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'view_only'
  });
  const [inviting, setInviting] = useState(false);
  
  // Integration form state
  const [azureForm, setAzureForm] = useState({
    azure_client_id: '',
    azure_client_secret: '',
    azure_tenant_id: 'common'
  });
  const [twilioForm, setTwilioForm] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_from: ''
  });
  const [showSecrets, setShowSecrets] = useState({
    azure: false,
    twilio: false
  });
  
  // Checklist form state
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemRequired, setNewItemRequired] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, outlookRes] = await Promise.all([
        api.get('/settings/user'),
        api.get('/outlook/status')
      ]);
      
      setUserSettings(settingsRes.data);
      setOutlookStatus(outlookRes.data);
      setName(settingsRes.data.name || '');
      setEmailSignature(settingsRes.data.email_signature || '');
      setDailyEmailCap(settingsRes.data.daily_email_cap || 100);
      
      // Load admin data if admin
      if (settingsRes.data.role === 'admin') {
        const [usersRes, logsRes, templatesRes, integrationsRes, invitesRes] = await Promise.all([
          api.get('/admin/users'),
          api.get('/admin/audit-logs?limit=50'),
          api.get('/admin/checklist-templates'),
          api.get('/admin/integration-settings'),
          api.get('/admin/users/invites')
        ]);
        setUsers(usersRes.data);
        setAuditLogs(logsRes.data);
        setChecklistTemplates(templatesRes.data);
        setIntegrationSettings(integrationsRes.data);
        setInvites(invitesRes.data.filter(i => i.status === 'pending'));
        
        // Pre-fill forms with existing values
        if (integrationsRes.data) {
          setAzureForm(prev => ({
            ...prev,
            azure_client_id: integrationsRes.data.azure_client_id || '',
            azure_tenant_id: integrationsRes.data.azure_tenant_id || 'common'
          }));
          setTwilioForm(prev => ({
            ...prev,
            twilio_account_sid: integrationsRes.data.twilio_account_sid || '',
            twilio_whatsapp_from: integrationsRes.data.twilio_whatsapp_from || ''
          }));
        }
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put('/settings/user', {
        name,
        email_signature: emailSignature,
        daily_email_cap: dailyEmailCap
      });
      toast.success('Settings saved');
      checkAuth();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleConnectOutlook = async () => {
    try {
      const res = await api.post('/outlook/connect');
      toast.info('Outlook Integration Setup', {
        description: 'See the instructions below to configure Azure App Registration',
        duration: 10000
      });
    } catch (error) {
      toast.error('Failed to initiate Outlook connection');
    }
  };

  const handleDisconnectOutlook = async () => {
    try {
      await api.post('/outlook/disconnect');
      toast.success('Outlook disconnected');
      loadSettings();
    } catch (error) {
      toast.error('Failed to disconnect Outlook');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}?role=${newRole}`);
      toast.success('User role updated');
      loadSettings();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleAddChecklistItem = async () => {
    if (!selectedStage || !newItemLabel.trim()) return;
    
    try {
      await api.post(`/admin/checklist-templates/${encodeURIComponent(selectedStage)}/items?label=${encodeURIComponent(newItemLabel)}&required=${newItemRequired}`);
      toast.success('Checklist item added');
      setShowAddItemDialog(false);
      setNewItemLabel('');
      setNewItemRequired(true);
      loadSettings();
    } catch (error) {
      toast.error('Failed to add checklist item');
    }
  };

  const handleDeleteChecklistItem = async (stage, itemIndex) => {
    try {
      await api.delete(`/admin/checklist-templates/${encodeURIComponent(stage)}/items/${itemIndex}`);
      toast.success('Checklist item removed');
      loadSettings();
    } catch (error) {
      toast.error('Failed to remove checklist item');
    }
  };

  const handleToggleItemRequired = async (stage, itemIndex, currentItems) => {
    const updatedItems = [...currentItems];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      required: !updatedItems[itemIndex].required
    };
    
    try {
      await api.put(`/admin/checklist-templates/${encodeURIComponent(stage)}`, updatedItems);
      toast.success('Checklist item updated');
      loadSettings();
    } catch (error) {
      toast.error('Failed to update checklist item');
    }
  };

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setInviting(true);
    try {
      await api.post('/admin/users/invite', inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteDialog(false);
      setInviteForm({ email: '', name: '', role: 'view_only' });
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.delete(`/admin/users/invites/${inviteId}`);
      toast.success('Invitation cancelled');
      loadSettings();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/admin/users/invites/${inviteId}/resend`);
      toast.success('Invitation resent');
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleSaveAzureSettings = async () => {
    try {
      await api.put('/admin/integration-settings', azureForm);
      toast.success('Azure settings saved');
      loadSettings();
    } catch (error) {
      toast.error('Failed to save Azure settings');
    }
  };

  const handleSaveTwilioSettings = async () => {
    try {
      await api.put('/admin/integration-settings', twilioForm);
      toast.success('Twilio settings saved');
      loadSettings();
    } catch (error) {
      toast.error('Failed to save Twilio settings');
    }
  };

  const handleClearIntegration = async (type) => {
    try {
      await api.delete(`/admin/integration-settings/${type}`);
      toast.success(`${type === 'azure' ? 'Azure' : 'Twilio'} settings cleared`);
      if (type === 'azure') {
        setAzureForm({ azure_client_id: '', azure_client_secret: '', azure_tenant_id: 'common' });
      } else {
        setTwilioForm({ twilio_account_sid: '', twilio_auth_token: '', twilio_whatsapp_from: '' });
      }
      loadSettings();
    } catch (error) {
      toast.error('Failed to clear settings');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="h-96 bg-muted rounded-xl"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="settings-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
            <Settings className="h-8 w-8" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your account and application settings
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="mr-2 h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="outlook" data-testid="outlook-tab">
              <Mail className="mr-2 h-4 w-4" />
              Outlook
            </TabsTrigger>
            {user?.role === 'admin' && (
              <>
                <TabsTrigger value="integrations" data-testid="integrations-tab">
                  <Plug className="mr-2 h-4 w-4" />
                  Integrations
                </TabsTrigger>
                <TabsTrigger value="checklists" data-testid="checklists-tab">
                  <ClipboardList className="mr-2 h-4 w-4" />
                  Checklists
                </TabsTrigger>
                <TabsTrigger value="users" data-testid="users-tab">
                  <Users className="mr-2 h-4 w-4" />
                  Users
                </TabsTrigger>
                <TabsTrigger value="audit" data-testid="audit-tab">
                  <FileText className="mr-2 h-4 w-4" />
                  Audit Logs
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>Update your personal information and preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                      {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold">{user?.name}</h3>
                    <p className="text-muted-foreground">{user?.email}</p>
                    <Badge variant="secondary" className="mt-1 capitalize">{user?.role}</Badge>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Display Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="profile-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={user?.email}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dailyCap">Daily Email Cap</Label>
                    <Input
                      id="dailyCap"
                      type="number"
                      value={dailyEmailCap}
                      onChange={(e) => setDailyEmailCap(parseInt(e.target.value))}
                      min={1}
                      max={500}
                      data-testid="daily-email-cap"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum emails you can send per day (recommended: 50-100)
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signature">Email Signature</Label>
                  <Textarea
                    id="signature"
                    value={emailSignature}
                    onChange={(e) => setEmailSignature(e.target.value)}
                    placeholder="Your email signature (HTML supported)"
                    rows={4}
                    data-testid="email-signature"
                  />
                </div>

                <Button onClick={handleSaveProfile} data-testid="save-profile-btn">
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Outlook Tab */}
          <TabsContent value="outlook">
            <Card>
              <CardHeader>
                <CardTitle>Microsoft Outlook Integration</CardTitle>
                <CardDescription>
                  Connect your Outlook account to send emails directly from the CRM
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Connection Status */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${outlookStatus?.connected ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                      {outlookStatus?.connected ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <AlertCircle className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {outlookStatus?.connected ? 'Outlook Connected' : 'Outlook Not Connected'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {outlookStatus?.message}
                      </p>
                    </div>
                  </div>
                  {outlookStatus?.connected ? (
                    <Button variant="outline" onClick={handleDisconnectOutlook} data-testid="disconnect-outlook-btn">
                      <Unlink className="mr-2 h-4 w-4" />
                      Disconnect
                    </Button>
                  ) : (
                    <Button onClick={handleConnectOutlook} data-testid="connect-outlook-btn">
                      <Link className="mr-2 h-4 w-4" />
                      Connect Outlook
                    </Button>
                  )}
                </div>

                <Separator />

                {/* Setup Instructions */}
                <div>
                  <h4 className="font-semibold mb-4">Azure App Registration Setup Instructions</h4>
                  <div className="space-y-4 text-sm">
                    <div className="p-4 border rounded-lg">
                      <h5 className="font-medium mb-2">Step 1: Create Azure App Registration</h5>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Azure Portal <ExternalLink className="inline h-3 w-3" /></a></li>
                        <li>Navigate to "App registrations"</li>
                        <li>Click "New registration"</li>
                        <li>Name your app (e.g., "Great Leaf CRM")</li>
                        <li>Select "Accounts in any organizational directory"</li>
                      </ol>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h5 className="font-medium mb-2">Step 2: Configure Redirect URIs</h5>
                      <p className="text-muted-foreground">Add the following redirect URIs:</p>
                      <code className="block mt-2 p-2 bg-muted rounded text-xs">
                        {window.location.origin}/api/outlook/callback
                      </code>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <h5 className="font-medium mb-2">Step 3: Add API Permissions</h5>
                      <p className="text-muted-foreground mb-2">Add these Microsoft Graph permissions:</p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                        <li><code className="bg-muted px-1 rounded">Mail.Send</code> - Send mail as the user</li>
                        <li><code className="bg-muted px-1 rounded">Mail.Read</code> - Read user mail</li>
                        <li><code className="bg-muted px-1 rounded">Mail.ReadWrite</code> - Read and write mail</li>
                        <li><code className="bg-muted px-1 rounded">User.Read</code> - Sign in and read user profile</li>
                      </ul>
                    </div>

                    <div className="p-4 border rounded-lg bg-primary/5">
                      <h5 className="font-medium mb-2">Step 4: Set Environment Variables</h5>
                      <p className="text-muted-foreground mb-2">Or use the Integrations tab to configure these settings directly in the app.</p>
                      <code className="block p-2 bg-muted rounded text-xs whitespace-pre">
{`MS_CLIENT_ID=your_client_id
MS_CLIENT_SECRET=your_client_secret
MS_TENANT_ID=common`}
                      </code>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab (Admin Only) */}
          {user?.role === 'admin' && (
            <TabsContent value="integrations">
              <div className="space-y-6">
                {/* Microsoft Azure / Outlook Integration */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${integrationSettings?.azure_configured ? 'bg-green-100 text-green-600 dark:bg-green-900/20' : 'bg-muted'}`}>
                          <Mail className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Microsoft Azure / Outlook</CardTitle>
                          <CardDescription>Configure Azure App credentials for Outlook email integration</CardDescription>
                        </div>
                      </div>
                      {integrationSettings?.azure_configured && (
                        <Badge className="bg-green-500">Configured</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="azure_client_id">Client ID</Label>
                        <Input
                          id="azure_client_id"
                          value={azureForm.azure_client_id}
                          onChange={(e) => setAzureForm({...azureForm, azure_client_id: e.target.value})}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          data-testid="azure-client-id"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="azure_tenant_id">Tenant ID</Label>
                        <Input
                          id="azure_tenant_id"
                          value={azureForm.azure_tenant_id}
                          onChange={(e) => setAzureForm({...azureForm, azure_tenant_id: e.target.value})}
                          placeholder="common or your-tenant-id"
                          data-testid="azure-tenant-id"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="azure_client_secret">Client Secret</Label>
                      <div className="relative">
                        <Input
                          id="azure_client_secret"
                          type={showSecrets.azure ? 'text' : 'password'}
                          value={azureForm.azure_client_secret}
                          onChange={(e) => setAzureForm({...azureForm, azure_client_secret: e.target.value})}
                          placeholder={integrationSettings?.azure_configured ? '••••••••••••••••' : 'Enter client secret'}
                          data-testid="azure-client-secret"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() => setShowSecrets({...showSecrets, azure: !showSecrets.azure})}
                        >
                          {showSecrets.azure ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Leave blank to keep existing secret. Get your credentials from the Azure Portal.
                      </p>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveAzureSettings} data-testid="save-azure-btn">
                        <Key className="mr-2 h-4 w-4" />
                        Save Azure Settings
                      </Button>
                      {integrationSettings?.azure_configured && (
                        <Button variant="outline" onClick={() => handleClearIntegration('azure')}>
                          Clear Settings
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Twilio / WhatsApp Integration */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${integrationSettings?.twilio_configured ? 'bg-green-100 text-green-600 dark:bg-green-900/20' : 'bg-muted'}`}>
                          <MessageSquare className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Twilio / WhatsApp</CardTitle>
                          <CardDescription>Configure Twilio credentials for WhatsApp notifications</CardDescription>
                        </div>
                      </div>
                      {integrationSettings?.twilio_configured && (
                        <Badge className="bg-green-500">Configured</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="twilio_account_sid">Account SID</Label>
                        <Input
                          id="twilio_account_sid"
                          value={twilioForm.twilio_account_sid}
                          onChange={(e) => setTwilioForm({...twilioForm, twilio_account_sid: e.target.value})}
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          data-testid="twilio-account-sid"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="twilio_whatsapp_from">WhatsApp From Number</Label>
                        <Input
                          id="twilio_whatsapp_from"
                          value={twilioForm.twilio_whatsapp_from}
                          onChange={(e) => setTwilioForm({...twilioForm, twilio_whatsapp_from: e.target.value})}
                          placeholder="whatsapp:+14155238886"
                          data-testid="twilio-whatsapp-from"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="twilio_auth_token">Auth Token</Label>
                      <div className="relative">
                        <Input
                          id="twilio_auth_token"
                          type={showSecrets.twilio ? 'text' : 'password'}
                          value={twilioForm.twilio_auth_token}
                          onChange={(e) => setTwilioForm({...twilioForm, twilio_auth_token: e.target.value})}
                          placeholder={integrationSettings?.twilio_configured ? '••••••••••••••••' : 'Enter auth token'}
                          data-testid="twilio-auth-token"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          onClick={() => setShowSecrets({...showSecrets, twilio: !showSecrets.twilio})}
                        >
                          {showSecrets.twilio ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Leave blank to keep existing token. Get your credentials from the Twilio Console.
                      </p>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveTwilioSettings} data-testid="save-twilio-btn">
                        <Key className="mr-2 h-4 w-4" />
                        Save Twilio Settings
                      </Button>
                      {integrationSettings?.twilio_configured && (
                        <Button variant="outline" onClick={() => handleClearIntegration('twilio')}>
                          Clear Settings
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Checklist Templates Tab (Admin Only) */}
          {user?.role === 'admin' && (
            <TabsContent value="checklists">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Pipeline Checklist Settings</CardTitle>
                      <CardDescription>Configure required checklist items for each pipeline stage</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {checklistTemplates.map((template) => (
                      <div key={template.template_id} className="border rounded-lg p-4" data-testid={`template-${template.stage}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold text-lg">{template.stage}</h4>
                          <Dialog open={showAddItemDialog && selectedStage === template.stage} onOpenChange={(open) => {
                            setShowAddItemDialog(open);
                            if (open) setSelectedStage(template.stage);
                          }}>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="outline" onClick={() => setSelectedStage(template.stage)}>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Item
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Add Checklist Item</DialogTitle>
                                <DialogDescription>Add a new item to {selectedStage} checklist</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                  <Label>Item Label</Label>
                                  <Input
                                    value={newItemLabel}
                                    onChange={(e) => setNewItemLabel(e.target.value)}
                                    placeholder="e.g., Verify income documents"
                                    data-testid="new-item-label"
                                  />
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Switch
                                    checked={newItemRequired}
                                    onCheckedChange={setNewItemRequired}
                                    id="required"
                                  />
                                  <Label htmlFor="required">Required to move to next stage</Label>
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
                                <Button onClick={handleAddChecklistItem} disabled={!newItemLabel.trim()} data-testid="save-checklist-item">
                                  Add Item
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </div>
                        
                        {template.items && template.items.length > 0 ? (
                          <div className="space-y-2">
                            {template.items.map((item, idx) => (
                              <div 
                                key={idx} 
                                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                                data-testid={`checklist-template-item-${template.stage}-${idx}`}
                              >
                                <div className="flex items-center gap-3">
                                  <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                                  <span>{item.label}</span>
                                  {item.required && (
                                    <Badge variant="secondary" className="text-xs">Required</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      checked={item.required}
                                      onCheckedChange={() => handleToggleItemRequired(template.stage, idx, template.items)}
                                      id={`required-${template.stage}-${idx}`}
                                    />
                                    <Label htmlFor={`required-${template.stage}-${idx}`} className="text-xs text-muted-foreground">
                                      Required
                                    </Label>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteChecklistItem(template.stage, idx)}
                                    data-testid={`delete-item-${template.stage}-${idx}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No checklist items for this stage
                          </p>
                        )}
                      </div>
                    ))}
                    
                    {checklistTemplates.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No checklist templates configured</p>
                        <p className="text-sm">Templates will be auto-created when you first use the pipeline</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Users Tab (Admin Only) */}
          {user?.role === 'admin' && (
            <TabsContent value="users">
              <div className="space-y-6">
                {/* Invite User Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Invite New User</CardTitle>
                        <CardDescription>Send an invitation to add a new team member</CardDescription>
                      </div>
                      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                        <DialogTrigger asChild>
                          <Button data-testid="invite-user-btn">
                            <UserPlus className="mr-2 h-4 w-4" />
                            Invite User
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Invite New User</DialogTitle>
                            <DialogDescription>
                              Send an email invitation to add a new team member.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="invite-name">Name</Label>
                              <Input
                                id="invite-name"
                                value={inviteForm.name}
                                onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                                placeholder="John Smith"
                                data-testid="invite-name-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="invite-email">Email</Label>
                              <Input
                                id="invite-email"
                                type="email"
                                value={inviteForm.email}
                                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                                placeholder="john@company.com"
                                data-testid="invite-email-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="invite-role">Access Level</Label>
                              <Select
                                value={inviteForm.role}
                                onValueChange={(v) => setInviteForm({...inviteForm, role: v})}
                              >
                                <SelectTrigger data-testid="invite-role-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">
                                    <div className="flex items-center gap-2">
                                      <Shield className="h-4 w-4 text-emerald-500" />
                                      Full Admin Access
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="view_only">
                                    <div className="flex items-center gap-2">
                                      <Eye className="h-4 w-4 text-blue-500" />
                                      View Only Access
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                {inviteForm.role === 'admin' 
                                  ? 'Full access to all features including user management and settings.'
                                  : 'Can view contacts, deals, and reports but cannot make changes.'}
                              </p>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                              Cancel
                            </Button>
                            <Button onClick={handleInviteUser} disabled={inviting} data-testid="send-invite-btn">
                              {inviting ? (
                                <>Sending...</>
                              ) : (
                                <>
                                  <Send className="mr-2 h-4 w-4" />
                                  Send Invitation
                                </>
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                </Card>

                {/* Pending Invites */}
                {invites.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-amber-500" />
                        Pending Invitations
                      </CardTitle>
                      <CardDescription>Invitations awaiting acceptance</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Access Level</TableHead>
                            <TableHead>Sent</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invites.map((invite) => (
                            <TableRow key={invite.invite_id}>
                              <TableCell className="font-medium">{invite.name}</TableCell>
                              <TableCell>{invite.email}</TableCell>
                              <TableCell>
                                <Badge className={invite.role === 'admin' ? 'bg-emerald-600' : 'bg-blue-600'}>
                                  {invite.role === 'admin' ? (
                                    <><Shield className="h-3 w-3 mr-1" /> Admin</>
                                  ) : (
                                    <><Eye className="h-3 w-3 mr-1" /> View Only</>
                                  )}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {new Date(invite.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResendInvite(invite.invite_id)}
                                    data-testid={`resend-invite-${invite.invite_id}`}
                                  >
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                    Resend
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCancelInvite(invite.invite_id)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`cancel-invite-${invite.invite_id}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* User List */}
                <Card>
                  <CardHeader>
                    <CardTitle>Team Members</CardTitle>
                    <CardDescription>Manage existing team members and their roles</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Outlook</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((u) => (
                          <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={u.picture} />
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                    {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                  </AvatarFallback>
                                </Avatar>
                                {u.name}
                              </div>
                            </TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>
                              <Select
                                value={u.role}
                                onValueChange={(v) => handleUpdateUserRole(u.user_id, v)}
                                disabled={u.user_id === user?.user_id}
                              >
                                <SelectTrigger className="w-[130px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">
                                    <div className="flex items-center gap-2">
                                      <Shield className="h-3 w-3 text-emerald-500" />
                                      Admin
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="manager">Manager</SelectItem>
                                  <SelectItem value="broker">Broker</SelectItem>
                                  <SelectItem value="view_only">
                                    <div className="flex items-center gap-2">
                                      <Eye className="h-3 w-3 text-blue-500" />
                                      View Only
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Badge variant={u.outlook_connected ? 'default' : 'secondary'}>
                                {u.outlook_connected ? 'Connected' : 'Not Connected'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {u.user_id === user?.user_id && (
                                <Badge variant="outline">You</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Audit Logs Tab (Admin Only) */}
          {user?.role === 'admin' && (
            <TabsContent value="audit">
              <Card>
                <CardHeader>
                  <CardTitle>Audit Logs</CardTitle>
                  <CardDescription>Track important actions in the system</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Action</TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Timestamp</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLogs.map((log) => (
                          <TableRow key={log.log_id}>
                            <TableCell>
                              <Badge variant="outline">{log.action}</Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {log.entity_type} / {log.entity_id.substring(0, 12)}...
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {log.user_id.substring(0, 12)}...
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {new Date(log.created_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                        {auditLogs.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                              No audit logs yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default SettingsPage;
